import type { SellerCollectsConfig } from '@b-stock/fe-core/bstock-next/queries'
import { StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy } from '@b-stock/listing-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

/**
 * The only shipment types that state, on their own, who moved the goods.
 *
 * A buyer reads their own order, not the seller's general arrangement, so a lot
 * the buyer actually collected must not be described as delivered just because
 * the storefront usually delivers.
 *
 * Deliberately only the two buyer-side types. `BINDING` cannot be read as
 * "B-Stock booked it" on a Seller Collects order: a buyer-pickup quote is
 * recorded as BINDING with a zero rate — that is exactly what
 * `isZeroRatePlaceholderQuote` detects, and what FP-3356 and FP-3446 exist to
 * stop the UI misreading. `FLAT_RATE`, `FREE` and `OTHER` say nothing about who
 * arranged the freight either. `SELLER_DELIVERS` has no order-level equivalent
 * at all. All of those defer to the storefront, which is the only source that
 * actually knows.
 */
const MODE_BY_SHIPMENT_TYPE: Partial<Record<ShipmentType, ShippingArrangedBy>> =
  {
    [ShipmentType.BuyerArranged]: ShippingArrangedBy.Buyer,
    [ShipmentType.BuyerPickup]: ShippingArrangedBy.Buyer,
  }

/**
 * How this order's freight was arranged, the order outranking its storefront.
 *
 * `shippingArrangedBy` is documented as who arranges freight for orders on
 * *this storefront* — a default, not a fact about any one order. Where an order
 * departs from that default, two surfaces reading the same field said opposite
 * things on one page: the contacts row said "You arrange collection" while the
 * first-win modal said shipping was booked through B-Stock.
 *
 * So both resolve the mode here. An order that names nothing B-Stock can read
 * falls back to the storefront, which is the behaviour every surface had before.
 */
export const resolveShippingArrangedBy = (
  config: SellerCollectsConfig | null | undefined,
  /** The order carries this as a plain string; anything outside the enum misses
   * the map and defers to the storefront, which is the intent. */
  shipmentType?: string | null
): ShippingArrangedBy | undefined =>
  (shipmentType
    ? MODE_BY_SHIPMENT_TYPE[shipmentType as ShipmentType]
    : undefined) ?? config?.shippingArrangedBy
